import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { isAuthenticated, isAdvisor, authPut } from "@/utils/auth";
import {
  Shield, Search, IndianRupee, FileText, CheckCircle2, Clock,
  Loader2, Lock, Pencil, X, Check, Calendar, MessageCircle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[₹,]/g, "")) || 0;
  return "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return d;
  }
};

const CATEGORY_COLORS = {
  Health: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  Life: "bg-cyan-500/15 text-cyan-700 border-cyan-500/30",
  Vehicle: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  PA: "bg-rose-500/15 text-rose-700 border-rose-500/30"
};

const shareCaseOnWhatsApp = (p, partner) => {
  const payout = p.payout_received
    ? `✅ Payout: Received${p.payout_amount ? ` ${fmtINR(p.payout_amount)}` : ""}${p.payout_date ? ` on ${fmtDate(p.payout_date)}` : ""}`
    : "⏳ Payout: Pending";
  const lines = [
    `🛡️ *${partner} — Case Update*`,
    `👤 Client: *${p.client_name}*`,
    `📋 ${p.insurance_company} — ${p.plan_name} (${p.insurance_category})`,
    `🔢 Policy No: ${p.policy_number}`,
    `💰 Premium: ${fmtINR(p.premium_amount)} (${p.payment_frequency || "Annual"})`,
    ...(p.sum_insured && p.sum_insured !== "Not specified" ? [`☂️ Sum Insured: ${p.sum_insured}`] : []),
    `📅 Issued: ${fmtDate(p.policy_start_date)}`,
    payout,
    ``,
    `— shared from Kavach Box`
  ];
  window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
};

const PayoutEditor = ({ policy, onSaved }) => {
  const [received, setReceived] = useState(policy.payout_received);
  const [amount, setAmount] = useState(policy.payout_amount || "");
  const [date, setDate] = useState(policy.payout_date || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await authPut(`/api/advisor/policies/${policy.id}/payout`, {
        payout_received: received,
        payout_amount: amount || null,
        payout_date: date || null
      });
      if (res.ok) {
        toast.success("Payout updated");
        onSaved({ ...policy, payout_received: received, payout_amount: amount, payout_date: date });
      } else {
        toast.error("Failed to update payout");
      }
    } catch {
      toast.error("Failed to update payout");
    }
    setSaving(false);
  };

  return (
    <div className="mt-3 p-3 rounded-xl bg-[#EDF1F8]/50 border border-[#D4A017]/30 space-y-3" data-testid={`payout-editor-${policy.id}`}>
      <div className="flex items-center gap-3">
        <button
          onClick={() => setReceived(!received)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
            received ? "bg-emerald-500/20 text-emerald-700 border border-emerald-500/40" : "bg-amber-500/15 text-amber-700 border border-amber-500/30"
          }`}
          data-testid={`payout-toggle-${policy.id}`}
        >
          {received ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
          {received ? "Payout Received" : "Payout Pending"}
        </button>
      </div>
      {received && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-[#3A4557] font-medium block mb-1">Payout amount (₹)</label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 4500"
              className="h-8 text-xs bg-white border-[#C9D4E6] text-[#1B2A4A]" data-testid={`payout-amount-${policy.id}`} />
          </div>
          <div>
            <label className="text-[10px] text-[#3A4557] font-medium block mb-1">Payout date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="h-8 text-xs bg-white border-[#C9D4E6] text-[#1B2A4A]" data-testid={`payout-date-${policy.id}`} />
          </div>
        </div>
      )}
      <Button onClick={save} disabled={saving} size="sm" className="btn-gold h-8 text-xs w-full" data-testid={`payout-save-${policy.id}`}>
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
        Save
      </Button>
    </div>
  );
};

export default function PartnerLedger() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const canEdit = isAuthenticated() && isAdvisor();

  useEffect(() => {
    axios.get(`${API}/api/partner/${token}`)
      .then((res) => setData(res.data))
      .catch(() => setInvalid(true))
      .finally(() => setLoading(false));
  }, [token]);

  const onPolicySaved = (updated) => {
    setEditingId(null);
    setData((prev) => {
      const policies = prev.policies.map((p) => (p.id === updated.id ? updated : p));
      const received = policies.filter((p) => p.payout_received);
      const num = (v) => parseFloat(String(v ?? "").replace(/[₹,]/g, "")) || 0;
      return {
        ...prev,
        policies,
        summary: {
          ...prev.summary,
          payout_received_count: received.length,
          payout_received_amount: received.reduce((s, p) => s + num(p.payout_amount), 0),
          payout_pending_count: policies.length - received.length
        }
      };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#A87608] animate-spin" />
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="glass-card p-8 text-center max-w-sm" data-testid="partner-invalid-link">
          <Lock className="h-10 w-10 text-rose-600 mx-auto mb-3" />
          <h1 className="text-[#1B2A4A] font-bold text-lg mb-1">Invalid or expired link</h1>
          <p className="text-[#3A4557] text-sm">This private page can only be opened with the correct link.</p>
        </div>
      </div>
    );
  }

  const { partner, summary, policies } = data;
  const q = search.trim().toLowerCase();
  const filtered = q
    ? policies.filter((p) =>
        [p.client_name, p.policy_number, p.plan_name, p.insurance_company].some((f) => (f || "").toLowerCase().includes(q))
      )
    : policies;

  const cards = [
    { label: "Total Cases", value: summary.total_cases, icon: FileText, color: "text-cyan-700" },
    { label: "Total Premium", value: fmtINR(summary.total_premium), icon: IndianRupee, color: "text-[#A87608]" },
    { label: "Payout Received", value: `${summary.payout_received_count} · ${fmtINR(summary.payout_received_amount)}`, icon: CheckCircle2, color: "text-emerald-700" },
    { label: "Payout Pending", value: summary.payout_pending_count, icon: Clock, color: "text-amber-700" }
  ];

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="partner-ledger-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FFFFFF] via-[#E7EDF6] to-[#EDF1F8] border-b border-[#C9D4E6]">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="h-5 w-5 text-[#A87608]" />
            <span className="text-[#2E3B57] text-xs font-bold tracking-widest uppercase">Kavach Box · Private Ledger</span>
          </div>
          <h1 className="text-[#1B2A4A] font-bold text-2xl sm:text-3xl" data-testid="partner-name">{partner}</h1>
          <p className="text-[#3A4557] text-sm mt-1">Shared business register — updated live from the CRM. View-only.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-5 space-y-5">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} className="glass-card p-4" data-testid={`partner-summary-${i}`}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Icon className={`h-4 w-4 ${c.color}`} />
                  <p className="text-[#3A4557] text-[11px] font-medium">{c.label}</p>
                </div>
                <p className={`font-bold text-lg ${c.color}`}>{c.value}</p>
              </div>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="h-4 w-4 text-[#3A4557] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search client, policy number or plan..."
            className="pl-9 bg-[#EDF1F8]/40 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557]"
            data-testid="partner-search-input"
          />
        </div>

        {/* Policies */}
        {filtered.length === 0 ? (
          <div className="glass-card p-10 text-center" data-testid="partner-empty-state">
            <FileText className="h-10 w-10 text-[#3A4557] mx-auto mb-3" />
            <p className="text-[#1B2A4A] font-bold">{policies.length === 0 ? "No cases recorded yet" : "No matches found"}</p>
            <p className="text-[#3A4557] text-sm mt-1">
              {policies.length === 0 ? "Policies tagged under this partner will appear here automatically." : "Try a different search."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((p) => (
              <div key={p.id} className="glass-card p-4" data-testid={`partner-policy-${p.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <p className="text-[#1B2A4A] font-bold text-sm">{p.client_name}</p>
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${CATEGORY_COLORS[p.insurance_category] || CATEGORY_COLORS.Health}`}>
                        {p.insurance_category}
                      </span>
                    </div>
                    <p className="text-[#2E3B57] text-xs">{p.insurance_company} — {p.plan_name}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[#A87608] font-bold text-sm">{fmtINR(p.premium_amount)}</p>
                    <p className="text-[#3A4557] text-[10px]">{p.payment_frequency || "Annual"}</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2.5 text-[11px] text-[#3A4557]">
                  <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{p.policy_number}</span>
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{fmtDate(p.policy_start_date)}</span>
                  {p.sum_insured && p.sum_insured !== "Not specified" && <span>SI: {p.sum_insured}</span>}
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#C9D4E6]">
                  {p.payout_received ? (
                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/15 text-emerald-700 border border-emerald-500/30" data-testid={`payout-status-${p.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Payout Received{p.payout_amount ? ` · ${fmtINR(p.payout_amount)}` : ""}{p.payout_date ? ` · ${fmtDate(p.payout_date)}` : ""}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-500/15 text-amber-700 border border-amber-500/30" data-testid={`payout-status-${p.id}`}>
                      <Clock className="h-3.5 w-3.5" />
                      Payout Pending
                    </span>
                  )}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => shareCaseOnWhatsApp(p, partner)}
                      className="flex items-center gap-1 text-[11px] font-medium text-emerald-700 hover:text-emerald-800 transition-colors"
                      data-testid={`whatsapp-share-btn-${p.id}`}
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      Share
                    </button>
                    {canEdit && (
                      <button
                        onClick={() => setEditingId(editingId === p.id ? null : p.id)}
                        className="flex items-center gap-1 text-[11px] text-[#2E3B57] hover:text-[#1B2A4A] transition-colors"
                        data-testid={`payout-edit-btn-${p.id}`}
                      >
                        {editingId === p.id ? <X className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                        {editingId === p.id ? "Close" : "Update payout"}
                      </button>
                    )}
                  </div>
                </div>

                {canEdit && editingId === p.id && <PayoutEditor policy={p} onSaved={onPolicySaved} />}
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-[#3A4557] text-[11px] flex items-center justify-center gap-1.5 pt-2">
          <Lock className="h-3 w-3" />
          Private link — please keep it within the group.
        </p>
      </div>
    </div>
  );
}
